import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogData } from 'src/app/models/dialog/dialog.model';
import { SearchCustomerDialogComponent } from '../search-customer-dialog/search-customer-dialog.component';
import { AppState, getLoadingPartyState, loadedCategoriesListPartyState, loadedPropertiesListPartyState, loadedSupplierListTotalState, loadedSupplierPartyListState, loadedSuppliersTableConfig } from 'src/app/store/reducers';
import { select, Store } from '@ngrx/store';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { map, skip } from 'rxjs/operators';
import { setCategories, setColumnInfoCategoriesProperties, setProperties } from 'src/app/shared/utils/tableUtils';
import { Observable, Subscription, zip } from 'rxjs';
import { SupplierPartyDTO } from 'src/app/models/Party/supplierPartyDTO';
import { CategoryDTO } from 'src/app/models/Categories/categoryDTO';
import { PropertyDTO } from 'src/app/models/Properties/propertyDTO';
import { getCategoriesForListByUsage, getPropertiesForListByUsage, getSupplierPartyList, getSupplierTableConfig, resetPartyState, saveSupplierTableConfig } from 'src/app/store/actions/parties/parties.actions';
import { CHIP_STYLE, getChipStyle } from 'src/app/shared/utils/customTableUtils';

const USAGE_CODE = "SEARCH_SUPPLIER";

@Component({
  selector: 'app-search-supplier-dialog',
  templateUrl: './search-supplier-dialog.component.html',
  styleUrls: ['./search-supplier-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SearchSupplierDialogComponent implements OnInit, OnDestroy {

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public isLoading$: Observable<boolean>;
  public supplierList$: Observable<SupplierPartyDTO[]>;
  public supplierList: SupplierPartyDTO[];
  public totalSuppliers$: Observable<number>;
  private categories$: Observable<CategoryDTO[]>;
  private properties$: Observable<PropertyDTO[]>;
  private multiSubs: Subscription;
  private loadedSuppliersSub: Subscription;

  public selectedRow: object = {};

  public tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Suppliers.code",
      field: "code",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Suppliers.supplierCode",
      field: "supplierCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Suppliers.Status",
      field: "statusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Suppliers.VatIN",
      field: "vatin",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Suppliers.FullName",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Suppliers.Salutation",
      field: "salutationDescription",
      fieldType: FieldType.STRING,
    },
    {
      header: "Suppliers.Birthday",
      field: "birthday",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATE,
      hidden: true,
    },
    {
      header: "Suppliers.Phone",
      field: "contacts[PHONE].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Suppliers.Email",
      field: "contacts[EMAIL].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Suppliers.Mobile",
      field: "contacts[MOBILE].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Suppliers.StreetName",
      field: "address.street",
      fieldType: FieldType.STRING,
    },
    {
      header: "Suppliers.postalZipCode",
      field: "address.postalZipCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "Suppliers.City",
      field: "address.city",
      fieldType: FieldType.STRING,
    },
    {
      header: "Suppliers.Country",
      field: "address.isoCountryCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "Suppliers.AddressDesc",
      field: "address.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Suppliers.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Suppliers.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialogRef: MatDialogRef<SearchSupplierDialogComponent>,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    this.isLoading$ = this.store.pipe(select(getLoadingPartyState));
    this.supplierList$ = this.store.pipe(select(loadedSupplierPartyListState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.totalSuppliers$ = this.store.pipe(select(loadedSupplierListTotalState));
    this.columnInfo$ = this.store.pipe(select(loadedSuppliersTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );
    this.getSupplierCategoriesProperties();
  }

  public onGetTableData(tableEvent: TableEvent): void {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getSuppliersList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  public getSuppliersList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ): void {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSupplierPartyList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  public onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.store.dispatch(
      saveSupplierTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }

  private getSupplierCategoriesProperties(): void {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig();
      this.getSuppliersList();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );

    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  public getTableConfig(): void {
    this.store.dispatch(
      getSupplierTableConfig({
        options: { code: USAGE_CODE, settingType: ConfigSettingType.TABLE_CONFIG },
      })
    );
  }

  setTableCustomElements() {
    this.loadedSuppliersSub = this.supplierList$
      .pipe(skip(1))
      .subscribe((suppliers: SupplierPartyDTO[]) => {
        this.supplierList = suppliers;
        if (this.supplierList !== undefined && this.supplierList !== null && this.supplierList?.length > 0) {
          this.supplierList = this.supplierList.map((supplier) => {
            let treatedSupplier = supplier;
            treatedSupplier = this.setCustomStatusChip(treatedSupplier);
            return treatedSupplier;
          });

        }
      });
  }

  private setCustomStatusChip(supplier: any) {
    const defaultSupplier = {
      ...supplier,
    };
    const { statusDescription, statusCode } = supplier;
    if (statusDescription === undefined) {
      return defaultSupplier;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    if (statusDescription !== undefined && statusDescription !== null) {
      if (statusCode !== undefined && statusCode !== null) {
        switch (statusCode) {
          case 'ACTIVE':
            style = CHIP_STYLE.SUCCESS;
            break;
          case 'DISABLED':
            style = CHIP_STYLE.INACTIVE;
            break;
          case 'BLOCKED':
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);
      let statusChip = {
        ...supplier,
        statusDescription: {
          value: statusDescription,
          icon: styleSettings.icon,
          style: styleSettings.style,
        },
      };
      return statusChip;
    }
  }

  public isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedRow).length === 0;
  }

  public onSelectedRow(event): void {
    const { row, dblClick } = event;
    this.selectedRow = row;
    if (dblClick) {
      this.onConfirmClick();
    }
  }

  public onConfirmClick(): void {
    this.dialogRef.close(this.selectedRow);
  }

  ngOnDestroy() {
    if (this.loadedSuppliersSub) {
      this.loadedSuppliersSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    this.store.dispatch(resetPartyState());
  }
}